import {
  AutoShapeDeepFragment,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';

/**
 * @deprecated Virtual folders are no longer needed since we auto-create real folders.
 * Keeping for backwards compatibility but should not be used.
 */
export interface VirtualFolderNode {
  __typename: 'VirtualFolder';
  id: string;
  name: string;
}

/**
 * A node in the folder hierarchy tree.
 */
export interface HierarchyNode {
  id: string;
  name: string; // Display name (last path segment)
  fullPath: string; // Full path for identification
  isVirtual: boolean; // True if auto-created parent
  type: 'folder' | 'autoshape' | 'virtual';
  originalNode?: ShapeFolderDeepFragment | AutoShapeDeepFragment;
  children: HierarchyNode[]; // Sub-folders and autoshapes
}

/**
 * A flattened item ready for virtualization, with its level in the hierarchy.
 */
export interface FlattenedHierarchyItem {
  id: string;
  node: ShapeFolderDeepFragment | AutoShapeDeepFragment;
  level: number;
  isVirtual: boolean; // Always false now, kept for backwards compatibility
  displayName: string;
  fullPath: string;
  hasChildren: boolean; // True if this folder has any children (folders or shapes)
}

/**
 * A flattened shape item ready for virtualization.
 */
export interface FlattenedShapeItem {
  id: string;
  node: ShapeDeepFragment | SheetShapeDeepFragment;
  level: number;
  parentPath: string | null; // The path of the parent folder
}

/**
 * Combined type for virtualization items.
 */
export type VirtualizedItem =
  | { type: 'folder'; item: FlattenedHierarchyItem }
  | { type: 'shape'; item: FlattenedShapeItem };

/**
 * Extracts the display name (last segment) from a path.
 * e.g., "Floor 1/Rooms" -> "Rooms"
 */
export function getDisplayName(fullPath: string): string {
  const segments = fullPath.split('/');
  return segments[segments.length - 1];
}

/**
 * Extracts the parent path from a full path.
 * e.g., "Floor 1/Rooms" -> "Floor 1"
 * e.g., "Floor 1" -> null
 */
export function getParentPath(fullPath: string): string | null {
  const lastSlashIndex = fullPath.lastIndexOf('/');
  if (lastSlashIndex === -1) {
    return null;
  }
  return fullPath.substring(0, lastSlashIndex);
}

/**
 * Gets all ancestor paths for a given path.
 * e.g., "A/B/C" -> ["A", "A/B"]
 */
export function getAncestorPaths(fullPath: string): string[] {
  const segments = fullPath.split('/');
  const ancestors: string[] = [];
  for (let i = 1; i < segments.length; i++) {
    ancestors.push(segments.slice(0, i).join('/'));
  }
  return ancestors;
}

/**
 * Compares folder/shape names with numeric suffix support.
 */
function compareNameWithNumber(a: string, b: string): number {
  const regex = /(\d+)$/;
  const aMatch = a.trim().match(regex);
  const bMatch = b.trim().match(regex);

  if (aMatch && bMatch) {
    const aNumber = parseInt(aMatch[1], 10);
    const bNumber = parseInt(bMatch[1], 10);
    return aNumber - bNumber;
  }

  if (aMatch && !bMatch) return 1;
  if (!aMatch && bMatch) return -1;

  return a.localeCompare(b);
}

/**
 * Builds a hierarchy tree from flat folders and autoshapes.
 * Only includes real folders and autoshapes (no virtual nodes).
 */
// eslint-disable-next-line complexity
export function buildFolderHierarchy(
  folders: ShapeFolderDeepFragment[],
  autoShapes: AutoShapeDeepFragment[]
): HierarchyNode[] {
  // Map of full path -> HierarchyNode
  const nodesByPath = new Map<string, HierarchyNode>();

  // Create nodes only for real folders
  for (const folder of folders) {
    nodesByPath.set(folder.name, {
      id: folder.id,
      name: getDisplayName(folder.name),
      fullPath: folder.name,
      isVirtual: false,
      type: 'folder',
      originalNode: folder,
      children: [],
    });
  }

  // Create nodes for autoshapes
  for (const autoShape of autoShapes) {
    nodesByPath.set(autoShape.name, {
      id: autoShape.id,
      name: getDisplayName(autoShape.name),
      fullPath: autoShape.name,
      isVirtual: false,
      type: 'autoshape',
      originalNode: autoShape,
      children: [],
    });
  }

  // Build the tree structure by linking children to parents
  const rootNodes: HierarchyNode[] = [];

  for (const [path, node] of nodesByPath) {
    const parentPath = getParentPath(path);
    if (parentPath === null) {
      // This is a root node
      rootNodes.push(node);
    } else {
      const parentNode = nodesByPath.get(parentPath);
      if (parentNode) {
        parentNode.children.push(node);
      }
    }
  }

  // Sort children at each level
  function sortChildren(nodes: HierarchyNode[]): void {
    nodes.sort((a, b) => compareNameWithNumber(a.name, b.name));
    for (const node of nodes) {
      sortChildren(node.children);
    }
  }

  sortChildren(rootNodes);
  rootNodes.sort((a, b) => compareNameWithNumber(a.name, b.name));

  return rootNodes;
}

/**
 * Flattens the hierarchy tree for virtualization, respecting expanded state.
 * Returns items in the order they should be rendered.
 */
export function flattenHierarchyForVirtualization(
  hierarchy: HierarchyNode[],
  expandedPaths: Set<string>,
  shapesIndexedByFolder: Map<
    string,
    Array<ShapeDeepFragment | SheetShapeDeepFragment>
  >,
  autoShapes: AutoShapeDeepFragment[]
): VirtualizedItem[] {
  const result: VirtualizedItem[] = [];

  function flattenNode(node: HierarchyNode, level: number): void {
    // Get shapes for this folder/autoshape
    const shapes = node.originalNode
      ? shapesIndexedByFolder.get(node.originalNode.id) || []
      : [];

    // Check if this node has any children (folders or shapes)
    const hasChildren = node.children.length > 0 || shapes.length > 0;

    // Create the folder node representation
    // Ensure __typename is present (GraphQL fragments may not always include it)
    let folderNode: ShapeFolderDeepFragment | AutoShapeDeepFragment;
    if (node.type === 'folder') {
      folderNode = {
        ...node.originalNode!,
        __typename: 'ShapeFolder' as const,
      } as ShapeFolderDeepFragment;
    } else {
      // autoshape
      folderNode = {
        ...node.originalNode!,
        __typename: 'AutoShape' as const,
      } as AutoShapeDeepFragment;
    }

    // Add the folder/autoshape itself
    result.push({
      type: 'folder',
      item: {
        id: node.id,
        node: folderNode,
        level,
        isVirtual: node.isVirtual,
        displayName: node.name,
        fullPath: node.fullPath,
        hasChildren,
      },
    });

    // If expanded, add children
    if (expandedPaths.has(node.fullPath)) {
      // First add child folders/autoshapes
      for (const child of node.children) {
        flattenNode(child, level + 1);
      }

      // Then add shapes for this folder
      for (const shape of shapes) {
        result.push({
          type: 'shape',
          item: {
            id: shape.id,
            node: shape,
            level: level + 1,
            parentPath: node.fullPath,
          },
        });
      }
    }
  }

  // Flatten all root nodes
  for (const rootNode of hierarchy) {
    flattenNode(rootNode, 0);
  }

  return result;
}

/**
 * Collects all shapes from a folder and its descendants recursively.
 */
export function collectAllShapesInHierarchy(
  node: HierarchyNode,
  shapesIndexedByFolder: Map<
    string,
    Array<ShapeDeepFragment | SheetShapeDeepFragment>
  >
): Array<ShapeDeepFragment | SheetShapeDeepFragment> {
  const allShapes: Array<ShapeDeepFragment | SheetShapeDeepFragment> = [];

  // Add shapes from this folder
  if (node.originalNode) {
    const shapes = shapesIndexedByFolder.get(node.originalNode.id) || [];
    allShapes.push(...shapes);
  }

  // Recursively add shapes from children
  for (const child of node.children) {
    allShapes.push(
      ...collectAllShapesInHierarchy(child, shapesIndexedByFolder)
    );
  }

  return allShapes;
}

/**
 * Finds a hierarchy node by its path.
 */
export function findNodeByPath(
  hierarchy: HierarchyNode[],
  path: string
): HierarchyNode | undefined {
  for (const node of hierarchy) {
    if (node.fullPath === path) {
      return node;
    }
    const found = findNodeByPath(node.children, path);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Finds a hierarchy node by its ID (folder ID, autoshape ID, or virtual ID).
 */
export function findNodeById(
  hierarchy: HierarchyNode[],
  id: string
): HierarchyNode | undefined {
  for (const node of hierarchy) {
    if (node.id === id) {
      return node;
    }
    const found = findNodeById(node.children, id);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Gets all descendant paths of a node (for expanding/collapsing subtrees).
 */
export function getDescendantPaths(node: HierarchyNode): string[] {
  const paths: string[] = [];
  for (const child of node.children) {
    paths.push(child.fullPath);
    paths.push(...getDescendantPaths(child));
  }
  return paths;
}

/**
 * @deprecated Virtual folders are no longer used. Always returns false.
 * Kept for backwards compatibility.
 */
export function isVirtualFolderNode(
  node:
    | ShapeFolderDeepFragment
    | AutoShapeDeepFragment
    | VirtualFolderNode
    | ShapeDeepFragment
    | SheetShapeDeepFragment
): node is VirtualFolderNode {
  return (node as VirtualFolderNode).__typename === 'VirtualFolder';
}

/**
 * Gets the full path for a folder or autoshape node.
 * For regular nodes, returns the name. For shapes, returns the parent folder path.
 */
export function getFullPathForNode(
  node: ShapeFolderDeepFragment | AutoShapeDeepFragment
): string {
  return node.name;
}

/**
 * Checks if a path is a descendant of another path.
 * e.g., isDescendantPath("A", "A/B/C") -> true
 * e.g., isDescendantPath("A/B", "A") -> false
 * e.g., isDescendantPath("A", "A") -> false (not a descendant of itself)
 */
export function isDescendantPath(
  ancestorPath: string,
  potentialDescendantPath: string
): boolean {
  if (ancestorPath === potentialDescendantPath) {
    return false; // A path is not a descendant of itself
  }
  return potentialDescendantPath.startsWith(ancestorPath + '/');
}

/**
 * Computes the new path for a folder/autoshape when moved into another folder.
 * e.g., computeNewPath("SubFolder", "ParentFolder") -> "ParentFolder/SubFolder"
 * e.g., computeNewPath("A/B/SubFolder", "NewParent") -> "NewParent/SubFolder"
 *
 * @param draggedPath The current full path of the item being dragged
 * @param targetFolderPath The full path of the target folder (null if moving to root)
 * @returns The new full path for the dragged item
 */
export function computeNewPath(
  draggedPath: string,
  targetFolderPath: string | null
): string {
  const displayName = getDisplayName(draggedPath);

  if (targetFolderPath === null) {
    // Moving to root level
    return displayName;
  }

  return `${targetFolderPath}/${displayName}`;
}

/**
 * Checks if a folder can be dropped on a target.
 * Returns false if:
 * - The target is the same as the dragged folder
 * - The target is a descendant of the dragged folder (can't move folder into its own subfolder)
 *
 * @param draggedFolderPath The path of the folder being dragged
 * @param targetPath The path of the drop target (null for root)
 * @returns true if the drop is valid
 */
export function canDropFolderOnTarget(
  draggedFolderPath: string,
  targetPath: string | null
): boolean {
  if (targetPath === null) {
    // Can always drop at root level (unless already at root)
    return getParentPath(draggedFolderPath) !== null;
  }

  // Can't drop on itself
  if (draggedFolderPath === targetPath) {
    return false;
  }

  // Can't drop into a descendant
  if (isDescendantPath(draggedFolderPath, targetPath)) {
    return false;
  }

  // Check if already in this folder (no change needed)
  const currentParentPath = getParentPath(draggedFolderPath);
  if (currentParentPath === targetPath) {
    return false; // Already in this folder
  }

  return true;
}
